import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useHeaderContext } from '../../../../lib/context/HeaderContext';
import styles from './LocaleSwitcher.module.scss';
import { Region } from '../../compound/Header/Header.types';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import { classes } from '../../../../lib/utils/cssUtils';
import PureLink from '../../../common/Link/PureLink';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';
import { useDevice } from '../../../../lib/hooks/device';
import { useId } from '../../../../lib/hooks/useId';
import useFeature from '../../../../lib/hooks/useFeature';
import { SCALE } from '../../../common/Image/ImageScale';
import PureImage from '../../../common/Image/PureImage';
import { Cookies, FeatureToggleCodename } from '../../../../lib/types/enums';
import setCookie from '../../../../lib/utils/cookies/setCookie';
import { replaceAll } from '../../../../lib/utils/functional';
import { useScorm } from '../../../../lib/context/ScormContext';

const MAX_ROWS_PER_COLUMN = 12;

const groupIntoColumns = (regions?: Region[]) =>
  regions?.reduce<Region[][]>((columns, region) => {
    const itemsInRegion = region.locales.length + 1;
    const lastColumn = columns[columns.length - 1] || [];
    const lastColumnLength = lastColumn.reduce((acc, r) => acc + r.locales.length + 1, 0);

    if (lastColumnLength && lastColumnLength + itemsInRegion <= MAX_ROWS_PER_COLUMN) {
      return [...columns.slice(0, -1), [...lastColumn, region]];
    }

    return [...columns, [region]];
  }, []);

const LocaleSwitcher = () => {
  const switcherEnabled = useFeature(FeatureToggleCodename.localeSwitcher);
  const { regions, localeSwitcherLabel, localeSwitcherIcon } = useHeaderContext();
  const [isExpanded, setIsExpanded, toggleExpanded] = useMobileExpanded(false);
  const { downloadsList } = useScorm();
  const device = useDevice();
  const localesRefs = useRef<Record<string, HTMLAnchorElement>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const floatingContainerRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const { pathname } = useLocation();
  const currentLocale = regions?.flatMap(({ locales }) => locales).find(({ path }) => pathname.includes(path));

  const groupedRegions = useMemo(() => groupIntoColumns(regions), [regions]);

  const handleListElementKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    const localeId = (e.target as HTMLAnchorElement).attributes.getNamedItem('data-id')?.value;
    const valuesArray = Object.values(localesRefs.current);
    const keyIndex = Object.keys(localesRefs.current).findIndex(el => el === localeId);

    if (!regions?.length) {
      return;
    }

    switch (e.key) {
      case 'Escape':
      case ' ':
      case 'Space':
      case 'Tab':
        setIsExpanded(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        valuesArray[keyIndex + 1]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        valuesArray[keyIndex - 1]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        valuesArray[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        valuesArray[valuesArray.length - 1]?.focus();
        break;
      default:
        break;
    }
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'Space':
        e.preventDefault();
        toggleExpanded();
        if (!isExpanded) {
          setTimeout(() => Object.values(localesRefs.current)[0]?.focus());
        }
        break;
      default:
        break;
    }
  };

  const focusListener = useCallback((event: FocusEvent) => {
    event.stopPropagation();

    if (!containerRef.current?.contains(event.relatedTarget as Node)) {
      setIsExpanded(false);
    }
  }, []);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const { pathname: linkPathname } = new URL(e.currentTarget.href, window.origin);
    const hrefLocale = replaceAll('/', '')(linkPathname.match(/\/[a-z]{2}-[a-z]+\/?/gi)?.[0] ?? '');
    if (hrefLocale) {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      setCookie(Cookies.appPreferredLocale, hrefLocale, date);
    }

    return true;
  };

  useEffect(() => {
    if (device === 'desktop') {
      containerRef.current?.addEventListener('focusout', focusListener);
    }

    return () => containerRef.current?.removeEventListener('focusout', focusListener);
  }, []);

  useEffect(() => {
    setIsExpanded(false);
  }, [device]);

  useEffect(() => {
    if (device === 'desktop' && floatingContainerRef && floatingContainerRef.current) {
      const { left } = floatingContainerRef.current.getBoundingClientRect();

      if (left < 0) {
        floatingContainerRef.current.style.transform = `translateX(${Math.abs(left) + 50}px)`;
      }
    }
  }, [downloadsList]);

  if (!switcherEnabled || !groupedRegions || !groupedRegions.length) {
    return null;
  }

  return (
    <div className={classes(styles.container, { [styles.expanded]: isExpanded })} ref={containerRef}>
      <button
        className={styles.titleContainer}
        onClick={toggleExpanded}
        ref={buttonRef}
        onKeyDown={handleButtonKeyDown}
        aria-expanded={isExpanded}
        aria-haspopup
        aria-controls={id}
      >
        {!!localeSwitcherIcon && <PureImage src={localeSwitcherIcon} scale={SCALE.ICON} />}
        {localeSwitcherLabel || currentLocale?.title}
        <LeftArrow className={styles.arrow} aria-hidden />
      </button>
      <div id={id} className={styles.floatingContainer} role="region" ref={floatingContainerRef}>
        {groupedRegions.map(groupedColumn => (
          <div key={`${id}-${groupedColumn[0].uniqueId}`} className={styles.selectorColumn}>
            {groupedColumn.map(region => (
              <div
                key={region.uniqueId}
                className={classes(styles.scrolledRegion)}
                aria-describedby={`${region.uniqueId}-title`}
              >
                <h4 className={styles.regionTitle} id={`${region.uniqueId}-title`}>
                  {region.title}
                </h4>
                <ul className={styles.localesList} role="listbox">
                  {region.locales.map(({ uniqueId, title, path }) => (
                    <li
                      key={uniqueId}
                      className={classes(styles.localeItem, {
                        [styles.currentLocaleItem]: uniqueId === currentLocale?.uniqueId,
                      })}
                      data-text={title}
                      role="option"
                      aria-selected={uniqueId === currentLocale?.uniqueId}
                    >
                      <PureLink
                        data-id={uniqueId}
                        forceNativeAnchor
                        href={path}
                        className={styles.localeItemLink}
                        ref={el => {
                          if (uniqueId !== currentLocale?.uniqueId) {
                            localesRefs.current[uniqueId] = el as HTMLAnchorElement;
                          }
                        }}
                        onKeyDown={handleListElementKeyDown}
                        onClick={handleLinkClick}
                        tabIndex={-1}
                      >
                        {title}
                      </PureLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocaleSwitcher;
